<template>
  <DialogLayout class-name="notify-recipient-dialog" @close="handleCancel" :style="{ width: '832px' }">
    <template #title>
      <div class="dialog-title-wrapper">
        <div class="dialog-title">{{ t('notify.recipientDialog.title') }}</div>
        <div class="dialog-subtitle">
          {{ t('notify.recipientDialog.subtitle', { title: props.title }) }}
        </div>
      </div>
    </template>

    <template #default>
      <div class="dialog-content">
        <!-- 左侧选择区 -->
        <div class="left-panel">
          <div class="search-box">
            <el-input
              v-model="searchKeyword"
              :placeholder="t('notify.recipientDialog.searchPlaceholder')"
              clearable
            >
              <template #prefix>
                <el-icon>
                  <Search />
                </el-icon>
              </template>
            </el-input>
          </div>

          <div class="selection-area">
            <!-- 默认发送 -->
            <div v-if="!searchKeyword && defaultRecipients && defaultRecipients.length" class="selection-group">
              <div class="group-header">{{ t('notify.recipientDialog.defaultSend') }}</div>
              <div class="checkbox-list">
                <el-checkbox
                  v-for="recipient in defaultRecipients"
                  :key="recipient.roleId"
                  :model-value="true"
                  :disabled="true"
                  class="checkbox-item"
                >
                  {{ recipient.name }}
                </el-checkbox>
              </div>
            </div>

            <!-- 角色 -->
            <div v-if="filteredRoles.length" class="selection-group">
              <div class="group-header">
                {{ t('notify.recipientDialog.roles') }} ({{ selectedRoleIds.length }}/{{ availableRoles.length }})
              </div>
              <div class="checkbox-list">
                <el-checkbox
                  v-for="role in filteredRoles"
                  :key="role.id"
                  v-model="selectedRoleIds"
                  :label="role.id"
                  :value="role.id"
                  class="checkbox-item"
                >
                  <span v-html="highlightText(getRoleName(role.id))"></span>
                </el-checkbox>
              </div>
            </div>

            <!-- 账号 -->
            <div v-if="filteredAccounts.length" class="selection-group">
              <div class="group-header">
                {{ t('notify.recipientDialog.accounts') }} ({{ selectedAccountIds.length }}/{{ accounts.length }})
              </div>
              <div class="checkbox-list">
                <el-checkbox
                  v-for="account in filteredAccounts"
                  :key="account.id"
                  v-model="selectedAccountIds"
                  :label="account.id"
                  :value="account.id"
                  class="checkbox-item account-item"
                >
                  <div class="account-info">
                    <span class="account-name" v-html="highlightText(account.name)"></span>
                    <span class="account-email" v-html="highlightText(account.email)"></span>
                  </div>
                </el-checkbox>
              </div>
            </div>

            <!-- 无搜索结果 -->
            <div v-if="searchKeyword && !filteredRoles.length && !filteredAccounts.length" class="no-results">
              <!-- <el-empty :description="t('common.emptyStates.noResults.title')" /> -->
              <div>
                <EmptyState :type="'search'" :title="t('job.list.emptyStates.noResults.title')" :description="t('job.list.emptyStates.noResults.description')" />
              </div>
            </div>
          </div>
        </div>

        <!-- 右侧已选区 -->
        <div class="right-panel">
          <div class="selected-header">{{ t('notify.recipientDialog.selected') }}</div>

          <div class="selected-list">
            <!-- 默认发送 -->
            <div v-if="defaultRecipients.length" class="selected-section">
              <div class="section-title">{{ t('notify.recipientDialog.defaultSend') }}</div>
              <div
                v-for="(recipient, index) in defaultRecipients"
                :key="`default-${recipient.roleId}`"
                class="selected-item default-item"
              >
                <div class="info">
                  <span>{{ recipient.name }}</span>
                  <el-icon class="lock-icon"><Lock /></el-icon>
                </div>
                <div class="line" v-if="index <= defaultRecipients.length - 2"></div>
              </div>
            </div>

            <!-- 已选角色 -->
            <div v-if="selectedRoles.length" class="selected-section">
              <div class="section-title">
                {{ t('notify.recipientDialog.roles') }} ({{ selectedRoles.length }})
              </div>
              <div
                v-for="(role, index) in selectedRoles"
                :key="`role-${role.id}`"
                class="selected-item"
              >
                <div class="info">
                  <span>{{ getRoleName(role.id) }}</span>
                  <el-icon class="remove-icon" @click="removeRole(role.id)">
                    <Close />
                  </el-icon>
                </div>
                <div class="line" v-if="index <= selectedRoles.length - 2"></div>
              </div>
            </div>

            <!-- 已选账号 -->
            <div v-if="selectedAccounts.length" class="selected-section">
              <div class="section-title">
                {{ t('notify.recipientDialog.accounts') }} ({{ selectedAccounts.length }})
              </div>
              <div
                v-for="(account, index) in selectedAccounts"
                :key="`account-${account.id}`"
                class="selected-item"
              >
                <div class="info">
                  <div class="account-info">
                    <span class="account-name">{{ account.name }}</span>
                    <span class="account-email">{{ account.email }}</span>
                  </div>
                  <el-icon class="remove-icon" @click="removeAccount(account.id)">
                    <Close />
                  </el-icon>
                </div>
                <div class="line" v-if="index <= selectedAccounts.length - 2"></div>
              </div>
            </div>
            <div style="height: 100%;" v-if="!defaultRecipients.length && !selectedRoles.length && !selectedAccounts.length" class="no-results">
              <EmptyState :type="'empty'" :title="t('job.list.emptyStates.noConditions.title')" />
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="footer-content">
        <div class="channel-selector">
          <span class="channel-label">{{ t('notify.recipientDialog.channels') }}</span>
          <el-checkbox-group v-model="selectedChannelIds">
            <el-checkbox
              v-for="channel in channelOptions"
              :key="channel.code"
              :value="channel.code"
              :label="channel.code"
            >
              {{ channel.name }}
            </el-checkbox>
          </el-checkbox-group>
        </div>
        <div class="footer-buttons">
          <el-button @click="handleCancel">{{ t('common.cancel') }}</el-button>
          <el-button type="primary" @click="handleConfirm">{{ t('common.save') }}</el-button>
        </div>
      </div>
    </template>
  </DialogLayout>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { ElMessage } from 'element-plus';
import { Search, Close, Lock } from '@element-plus/icons-vue';
import type { ChannelOption, NotifyRecipientDialogProps, NotifyRecipientResult } from './notify-recipient-dialog-types';
import { getAccountList } from '@/api/account';
import { SystemRole, System, UserRoleNameToIdMap, getRoleNameByIdMap } from '@/router/config/roles';
import { useUserStore } from '@/store/modules/user';
import DialogLayout from '@/components/business-components/dialog/dialog-layout.vue';
import {
  DefaultVirRole
} from '@/enum/message';
import { EmptyState } from '@/components/basic-components/empty-state';

const { t } = useI18n();
const userStore = useUserStore();

const props = defineProps<NotifyRecipientDialogProps>();
const defaultNameMap = {
  [DefaultVirRole.candidateOwner]: t('notify.recipientDialog.candidateOwner'),
  [DefaultVirRole.selectionOwner]: t('notify.recipientDialog.selectionOwner'),
}
const emit = defineEmits<{
  confirm: [result: NotifyRecipientResult];
  cancel: [];
  close: [];
}>();

const searchKeyword = ref('');
const selectedRoleIds = ref<number[]>([...props.selectedRoles]);
const selectedAccountIds = ref<number[]>([...props.selectedAccounts]);

interface Account {
  id: number;
  name: string;
  email: string;
}

const accounts = ref<Account[]>([]);
const defaultRecipients = computed(() => props.defaultRecipients);

// 获取当前系统的可用角色（去掉运营支持角色）
const availableRoles = computed(() => {
  const system = userStore.userSystem || System.business;
  return (SystemRole[system] || []).filter(
    (roleId) => roleId !== UserRoleNameToIdMap.ROLE_BUSINESS_OPERATION
  );
});

// 通知方式
const DEFAULT_CHANNEL_OPTIONS: ChannelOption[] = [
  { code: 1, name: t('notify.recipientDialog.channelInApp') },
  { code: 2, name: t('notify.recipientDialog.channelEmail') },
];
const channelOptions = computed(() => {
  return props.availableChannels && props.availableChannels.length
    ? props.availableChannels
    : DEFAULT_CHANNEL_OPTIONS;
});
const selectedChannelIds = ref<number[]>(
  props.selectedChannels && props.selectedChannels.length ? [...props.selectedChannels] : [1]
);

// 获取角色名称映射
const roleNameMap = getRoleNameByIdMap();

// 获取角色名称
const getRoleName = (roleId: number): string => {
  return roleNameMap[roleId] || '';
};

// 过滤角色
const filteredRoles = computed(() => {
  if (!searchKeyword.value) {
    return availableRoles.value.map(id => ({ id }));
  }
  return availableRoles.value
    .map(id => ({ id }))
    .filter(role => {
      const name = getRoleName(role.id).toLowerCase();
      return name.includes(searchKeyword.value.toLowerCase());
    });
});

// 过滤账号
const filteredAccounts = computed(() => {
  if (!searchKeyword.value) {
    return accounts.value;
  }
  const keyword = searchKeyword.value.toLowerCase();
  return accounts.value.filter(account => {
    return account.name.toLowerCase().includes(keyword) ||
           account.email.toLowerCase().includes(keyword);
  });
});

// 已选角色
const selectedRoles = computed(() => {
  return selectedRoleIds.value.map(id => ({ id }));
});

// 已选账号
const selectedAccounts = computed(() => {
  return accounts.value.filter(account => selectedAccountIds.value.includes(account.id));
});

// 移除角色
const removeRole = (roleId: number) => {
  const index = selectedRoleIds.value.indexOf(roleId);
  if (index > -1) {
    selectedRoleIds.value.splice(index, 1);
  }
};

// 移除账号
const removeAccount = (accountId: number) => {
  const index = selectedAccountIds.value.indexOf(accountId);
  if (index > -1) {
    selectedAccountIds.value.splice(index, 1);
  }
};

// 高亮搜索关键词
const highlightText = (text: string): string => {
  if (!searchKeyword.value) {
    return text;
  }
  const keyword = searchKeyword.value.trim();
  if (!keyword) {
    return text;
  }
  // 转义特殊字符
  const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escapedKeyword})`, 'gi');
  return text.replace(regex, '<span class="highlight">$1</span>');
};

const handleCancel = () => {
  emit('cancel');
};

const handleConfirm = () => {
  if (!selectedChannelIds.value.length) {
    ElMessage.error(t('notify.recipientDialog.channelRequired'));
    return;
  }
  emit('confirm', {
    roles: selectedRoleIds.value,
    accounts: selectedAccountIds.value,
    channels: selectedChannelIds.value,
  });
};

onMounted(async () => {
  // 加载账号列表
  try {
    const response = await getAccountList({
      pageSize: 1000,
      pageNum: 1,
    });
    if (response && response.records) {
      accounts.value = response.records.map((item: any) => ({
        id: item.id,
        name: item.name,
        email: item.email,
      }));
    }
  } catch (error) {
    console.error('Failed to load accounts:', error);
  }
});
</script>

<style lang="scss" scoped>
.notify-recipient-dialog {
  :deep(.dialog-layout-body) {
    padding: 0;
  }

  .footer-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;

    .channel-selector {
      display: flex;
      align-items: center;
      gap: 12px;

      .channel-label {
        font-size: 14px;
        color: #606266;
      }
    }

    .footer-buttons {
      display: flex;
      gap: 12px;
    }
  }

  .dialog-title-wrapper {
    .dialog-title {
      font-size: 16px;
      font-weight: 500;
      color: #303133;
    }

    .dialog-subtitle {
      margin-top: 8px;
      font-size: 14px;
      color: #606266;
      font-weight: normal;
    }
  }

  .dialog-content {
    display: flex;
    height: 600px;
    gap: 20px;

    .left-panel {
      flex: 1;
      display: flex;
      flex-direction: column;
      border: 1px solid #DCDFE6;
      border-radius: 4px;
      overflow: hidden;

      .search-box {
        padding: 12px;
        border-bottom: 1px solid #EBEEF5;
      }

      .selection-area {
        flex: 1;
        overflow-y: auto;
        padding: 12px;

        .no-results {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          color: #909399;
        }

        .selection-group {
          // margin-bottom: 20px;

          &:last-child {
            margin-bottom: 0;
          }

          .group-header {
            font-size: 14px;
            font-weight: 500;
            color: #88929B;
            margin-bottom: 2px;
            background: #F8F9FA;
            padding: 4px 0 4px 20px;
          }

          .checkbox-list {
            display: flex;
            flex-direction: column;
            margin-left: 20px;
            // gap: 8px;

            .checkbox-item {
              margin: 8px 0;
              height: auto;

              &.account-item {
                // padding: 8px 0;
                :deep(.el-checkbox__label) {
                  width: 100%;
                }
              }

              .account-info {
                display: flex;
                flex-direction: column;
                gap: 2px;

                .account-name {
                  font-size: 14px;
                  color: #303133;

                  :deep(.highlight) {
                    color: var(--el-color-primary);
                    font-weight: 500;
                  }
                }

                .account-email {
                  font-size: 12px;
                  color: #909399;

                  :deep(.highlight) {
                    color: var(--el-color-primary);
                    font-weight: 500;
                  }
                }
              }

              :deep(.highlight) {
                color: var(--el-color-primary);
                font-weight: 500;
              }
            }
          }
        }
      }
    }

    .right-panel {
      flex: 1;
      display: flex;
      flex-direction: column;
      border: 1px solid #DCDFE6;
      border-radius: 4px;
      overflow: hidden;
      background: #FFF;

      .selected-header {
        margin: 20px 20px 12px;
        font-size: 14px;
        font-weight: 500;
        height: 33px;
        line-height: 33px;
        color: #303133;
        // border-bottom: 1px solid #EBEEF5;
        background: #fff;
      }

      .selected-list {
        flex: 1;
        overflow-y: auto;
        padding: 0px 20px;

        .selected-section {

          &:last-child {
            margin-bottom: 0;
          }

          .section-title {
            font-size: 12px;
            color: #909399;
            margin-bottom: 8px;
            background: #F8F9FA;
            height: 24px;
            line-height: 24px;
            padding-left: 20px;
          }

          .selected-item {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            background: #fff;
            margin-bottom: 2px;
            font-size: 14px;
            color: #495057;
            // border-bottom: 1px solid #EAECF0;
            cursor: pointer;
            display: flex;
            flex-direction: column;
            .info {
              display: flex;
              flex-direction: row;
              align-items: center;
              justify-content: space-between;
              padding: 8px 20px;
              width: 100%;
              margin-bottom: 2px;
            }
            .line {
              width: 100%;
              height: 1px;
              background:#EAECF0;
            }
            &:hover {
              .info { 
                background: #F4EFFD;
                border-radius: 8px;
              }
            }
            &.default-item {
              &:hover {
                .info { 
                  background: #fff;
                  border-radius: 8px;
                  cursor: not-allowed;
                }
              }
            }

            &:last-child {
              margin-bottom: 0;
            }

            &.default-item {
              // background: #F5F7FA;
              color: #495057;
            }

            .account-info {
              flex: 1;
              display: flex;
              flex-direction: column;
              gap: 2px;

              .account-name {
                font-size: 14px;
                color: #495057;
              }

              .account-email {
                font-size: 12px;
                color: #6B7075;
              }
            }

            .remove-icon {
              cursor: pointer;
              color: #909399;
              font-size: 16px;

              &:hover {
                color: #F56C6C;
              }
            }

            .lock-icon {
              color: #C0C4CC;
              font-size: 16px;
            }
          }
        }
      }
    }
  }
}
</style>
